import type { StoreDailySeries } from "@/lib/store-daily-series";
import type { StoreDataAvailability } from "@/lib/store-data-availability-contract";
import { hasAvailableStoreData } from "@/lib/store-data-availability-contract";
import { storePeriodCustomerMetrics } from "@/lib/store-period-metrics";

/** Only configuration is saved in the document. Values always come from the
 * existing scoped daily-series resource; the board never stores KPI snapshots. */
export const DASHBOARD_BOARD_NODE = "dashboardBoard";
export const DASHBOARD_DAYS = [7, 14, 28] as const;
export type DashboardDays = typeof DASHBOARD_DAYS[number];
export const DASHBOARD_CARD_LIMIT = 40;
export const DASHBOARD_TONES = ["plain", "sand", "sage", "rose"] as const;
export const DASHBOARD_WIDTHS = ["small", "medium", "wide"] as const;
type SeriesKey = "orders" | "buyouts" | "orderValue" | "revenue" | "adSpend" | "adOrders" | "returns";
export const DASHBOARD_METRICS = [
  { key: "orders", label: "Заказы", unit: "quantity", series: "orders", description: "Единиц заказано за выбранный период." },
  { key: "orderValue", label: "Заказано на", unit: "money", series: "orderValue", description: "Сумма заказов по ценам кабинета за период." },
  { key: "buyouts", label: "Выкупы", unit: "quantity", series: "buyouts", description: "Выкупленные единицы за период." },
  { key: "revenue", label: "Выручка", unit: "money", series: "revenue", description: "Выручка по данным магазина за период. Это не прибыль." },
  { key: "adSpend", label: "Расход рекламы", unit: "money", series: "adSpend", description: "Расход на рекламу Ozon за период." },
  { key: "adOrders", label: "Заказы по рекламе", unit: "quantity", series: "adOrders", description: "Единицы, отнесённые Ozon к рекламе, включая модельные заказы." },
  { key: "returns", label: "Возвраты", unit: "quantity", series: "returns", description: "Возвращённые единицы за период." },
  { key: "check", label: "Средний чек", unit: "money", series: null, description: "Средняя цена единицы для покупателя, взвешенная по доступным заказам." },
  { key: "coinvest", label: "Соинвест", unit: "percent", series: null, description: "Доля скидки, оплаченная Ozon; считается по исходным суммам, а не среднему процентов." },
  { key: "stock", label: "Остаток на складе", unit: "quantity", series: null, description: "Остаток на складах Ozon по последнему обновлению. Не зависит от периода." },
  { key: "transit", label: "В пути на склад", unit: "quantity", series: null, description: "Единиц в пути на склад по последнему обновлению. Не зависит от периода." },
] as const;
export type DashboardMetricKey = typeof DASHBOARD_METRICS[number]["key"];
export type BoardPoint = { x: number; y: number };
export type DashboardCard = {
  id: string; kind: "metric" | "note" | "heading"; title: string; metric: DashboardMetricKey;
  days: DashboardDays | null; text: string; tone: typeof DASHBOARD_TONES[number]; width: typeof DASHBOARD_WIDTHS[number];
  position: BoardPoint; size: { width: number; height: number };
};
export type BoardArrow = { id: string; source: string; target: string };
export type BoardStroke = { id: string; points: BoardPoint[] };
export type DashboardBoardConfig = { title: string; days: DashboardDays; cards: DashboardCard[];
  arrows: BoardArrow[]; strokes: BoardStroke[]; viewport: BoardPoint & { zoom: number } };
const object = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const text = (value: unknown, max: number) => typeof value === "string" ? value.slice(0, max).replace(/\u0000/g, "") : "";
const isDays = (value: unknown): value is DashboardDays => (DASHBOARD_DAYS as readonly unknown[]).includes(value);
export const dashboardMetric = (key: DashboardMetricKey) => DASHBOARD_METRICS.find(metric => metric.key === key)!;
const bounded = (value: unknown, fallback: number, min: number, max: number) => typeof value === "number" && Number.isFinite(value) ? Math.round(Math.min(max, Math.max(min, value)) * 100) / 100 : fallback;
const point = (value: unknown, fallback: BoardPoint = { x: 0, y: 0 }): BoardPoint => { const p = object(value); return { x: bounded(p.x, fallback.x, -50000, 50000), y: bounded(p.y, fallback.y, -50000, 50000) }; };
const validId = (value: unknown): value is string => typeof value === "string" && /^[a-zA-Z0-9_-]{1,80}$/.test(value);
export const dashboardCardSize = (kind: DashboardCard["kind"], width: DashboardCard["width"] = "small") => ({ width: width === "wide" ? 520 : width === "medium" ? 360 : kind === "heading" ? 320 : 240, height: kind === "heading" ? 80 : kind === "note" ? 190 : 170 });

/** Bounded and deterministic on both server and client. Unknown metrics and
 * duplicate IDs are discarded rather than silently changing a saved card. */
export function normalizeDashboardBoard(raw: unknown): DashboardBoardConfig {
  const value = object(raw), seen = new Set<string>();
  const cards: DashboardCard[] = [];
  for (const input of Array.isArray(value.cards) ? value.cards.slice(0, DASHBOARD_CARD_LIMIT * 2) : []) {
    if (cards.length === DASHBOARD_CARD_LIMIT) break;
    const card = object(input), id = card.id;
    if (!validId(id) || seen.has(id) || (card.kind !== "metric" && card.kind !== "note" && card.kind !== "heading")) continue;
    if (card.kind === "metric" && !DASHBOARD_METRICS.some(metric => metric.key === card.metric)) continue;
    seen.add(id);
    const width = DASHBOARD_WIDTHS.includes(card.width as DashboardCard["width"]) ? card.width as DashboardCard["width"] : "small";
    const defaults = dashboardCardSize(card.kind, width), size = object(card.size);
    cards.push({ id, kind: card.kind, title: text(card.title, 80),
      metric: card.kind === "metric" ? card.metric as DashboardMetricKey : "orders",
      days: isDays(card.days) ? card.days : null, text: card.kind === "note" ? text(card.text, 4000) : "",
      tone: DASHBOARD_TONES.includes(card.tone as DashboardCard["tone"]) ? card.tone as DashboardCard["tone"] : "plain",
      width, position: point(card.position, { x: (cards.length % 3) * 280, y: Math.floor(cards.length / 3) * 220 }),
      size: { width: bounded(size.width, defaults.width, card.kind === "heading" ? 120 : 180, 900), height: bounded(size.height, defaults.height, card.kind === "heading" ? 48 : 150, 900) } });
  }
  const arrows: BoardArrow[] = [], edgeIds = new Set<string>(), pairs = new Set<string>();
  for (const input of Array.isArray(value.arrows) ? value.arrows.slice(0, 240) : []) {
    const edge = object(input);
    if (arrows.length >= 120) break;
    if (!validId(edge.id) || edgeIds.has(edge.id) || typeof edge.source !== "string" || typeof edge.target !== "string" || !seen.has(edge.source) || !seen.has(edge.target) || edge.source === edge.target) continue;
    const pair = JSON.stringify([edge.source, edge.target]);
    if (pairs.has(pair)) continue;
    edgeIds.add(edge.id); pairs.add(pair); arrows.push({ id: edge.id, source: edge.source, target: edge.target });
  }
  const strokes: BoardStroke[] = [], strokeIds = new Set<string>();
  for (const input of Array.isArray(value.strokes) ? value.strokes.slice(0, 160) : []) {
    const stroke = object(input);
    if (strokes.length >= 80) break;
    if (!validId(stroke.id) || strokeIds.has(stroke.id) || !Array.isArray(stroke.points)) continue;
    const points = stroke.points.slice(0, 600).filter(p => { const v = object(p); return typeof v.x === "number" && Number.isFinite(v.x) && typeof v.y === "number" && Number.isFinite(v.y); }).map(p => point(p));
    if (points.length < 2) continue;
    strokeIds.add(stroke.id); strokes.push({ id: stroke.id, points });
  }
  const viewport = object(value.viewport);
  return { title: text(value.title, 100).trim() || "Доска", days: isDays(value.days) ? value.days : 7, cards, arrows, strokes,
    viewport: { x: bounded(viewport.x, 24, -120000, 120000), y: bounded(viewport.y, 24, -120000, 120000), zoom: bounded(viewport.zoom, 1, 0.2, 2) } };
}

export function newDashboardCard(kind: DashboardCard["kind"], id: string): DashboardCard {
  return { id, kind, title: "", metric: "orders", days: null, text: "", tone: kind === "note" ? "sand" : "plain", width: "small", position: { x: 0, y: 0 }, size: dashboardCardSize(kind) };
}

export type DashboardData = { series: Partial<StoreDailySeries> | null;
  totals?: { stock: number; transit: number } | null; availability?: StoreDataAvailability };
export type DashboardMetricValue = { value: number | null; previous: number | null; change: number | null; current: boolean; days: number; anchor: string | null };

/** Windows follow the store's actual anchor. Missing/invalid values stay
 * unknown; previous windows are never zero-padded. Ratios reuse the existing
 * customer-price calculation used by StoreMetricsBlock. */
export function dashboardMetricValue(key: DashboardMetricKey, days: DashboardDays, data?: DashboardData): DashboardMetricValue {
  const current = key === "stock" || key === "transit";
  const anchor = data?.series?.anchor && /^\d{4}-\d{2}-\d{2}$/.test(data.series.anchor) ? data.series.anchor : null;
  const empty: DashboardMetricValue = { value: null, previous: null, change: null, current, days, anchor };
  if (!data) return empty;
  const available = hasAvailableStoreData(data.availability, data.series);
  if (current) {
    const value = data.totals?.[key as "stock" | "transit"];
    return { ...empty, value: typeof value === "number" && Number.isFinite(value) && (available || value !== 0) ? value : null };
  }
  const series = data.series;
  if (!series || !available) return empty;
  const len = series.orders?.length ?? 0;
  const valid = (values: number[] | undefined, from: number, to: number) => values && values.length >= to
    && values.slice(from, to).every(value => typeof value === "number" && Number.isFinite(value));
  const resolve = (back: number): number | null => {
    const to = len - days * back, from = to - days;
    if (from < 0 || to <= 0) return null;
    const definition = dashboardMetric(key);
    if (definition.series) {
      const values = series[definition.series as SeriesKey];
      return valid(values, from, to) ? values!.slice(from, to).reduce((sum, value) => sum + value, 0) : null;
    }
    if (!valid(series.customerPriceSum, from, to) || !valid(series.customerPriceCount, from, to)
      || key === "coinvest" && !valid(series.cabinetForCustomer, from, to)) return null;
    const customer = storePeriodCustomerMetrics(series, from, to);
    return key === "check" ? customer.check : customer.coinvest;
  };
  const value = resolve(0), previous = resolve(1);
  const change = value != null && previous != null ? key === "coinvest" ? value - previous
    : previous > 0 ? (value - previous) / previous * 100 : null : null;
  return { ...empty, value, previous, change };
}

const number = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });
export function formatDashboardValue(value: number | null, key: DashboardMetricKey): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const unit = dashboardMetric(key).unit;
  return `${number.format(value)}${unit === "money" ? " ₽" : unit === "percent" ? " %" : " шт."}`;
}
